import React, {useState} from 'react'
import {useDispatch} from 'react-redux'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link} from 'react-router-dom'
import {useFormik} from 'formik'
import * as auth from '../redux/AuthRedux'
import {login} from '../redux/AuthCRUD'
import {AxiosError} from 'axios'
import {PATH_AUTH} from '../../../routing/paths'

const REACT_APP_EMAIL = process.env.REACT_APP_EMAIL || ''
const REACT_APP_PASSWORD = process.env.REACT_APP_PASSWORD || ''

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required'),
  password: Yup.string()
    .min(8, 'Minimum 8 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Password is required'),
})

const initialValues = {
  email: REACT_APP_EMAIL,
  password: REACT_APP_PASSWORD,
}

/*
  Formik+YUP+Typescript:
  https://jaredpalmer.com/formik/docs/tutorial#getfieldprops
  https://medium.com/@maurice.de.beijer/yup-validation-and-typescript-and-formik-6c342578a20e
*/

export interface Error {
  error: {
    password: string[]
  }
}

export interface Success {
  success: boolean
  token: string
  user: User
  googleAnalytics: boolean
}

export interface User {
  id: number
  reference: string
  firstname: string
  lastname: string
  usernameCleverphone: string
  email: string
  lccmp: number
  domainName: string
  phone: string
  commercialName: string
  created_at: string
}

function isSuccess(response: Success | Error): response is Success {
  return (response as Success).success !== undefined
}

export function Login() {

  const [loading, setLoading] = useState(false)
  const dispatch = useDispatch()
  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      setTimeout(() => {
        login(values.email, values.password)
          .then(({data}: {data: Success | Error}) => {
            if (isSuccess(data)) {
              dispatch(auth.actions.login(data.token))
              window.location.reload()
              setLoading(false)
              setSubmitting(false)
            } else {

              setStatus(data.error.password.join(' '))
              setLoading(false)
              setSubmitting(false)
            }
          })
          .catch((error: AxiosError) => {
            if (error.response) {
              setStatus(error.response.data.message)
            } else if (error.request) {
              setStatus('Erreur de serveur: aucune réponse reçu')
            } else {
              setStatus('Erreur de configuration de la requete')
            }
            setLoading(false)
            setSubmitting(false)
          })
      }, 1000)
    },
  })

  return (
    <form
      className="form w-100"
      onSubmit={formik.handleSubmit}
      noValidate
      id="kt_login_signin_form"
    >
      {/* begin::Heading */}
      <div className="text-center mb-10">
        <h1 className="text-dark mb-3">Bienvenue sur MyWebforce</h1>
      </div>
      {/* begin::Heading */}

      {formik.status && (
        <div className="mb-lg-15 alert alert-danger">
          <div className="alert-text font-weight-bold">{formik.status}</div>
        </div>
      )}

      {/* begin::Form group */}
      <div className="fv-row mb-10">
        <label className="form-label fs-6 fw-bolder text-dark">Email</label>
        <input type="email"
               autoComplete="off"
               placeholder="email"
               {...formik.getFieldProps('email')}
               className={clsx(
                 'form-control form-control-lg form-control-solid',
                 {'is-invalid': formik.touched.email && formik.errors.email},
                 {
                   'is-valid': formik.touched.email && !formik.errors.email,
                 },
               )}

        />
        {formik.touched.email && formik.errors.email && (
          <div className="fv-plugins-message-container invalid-feedback">
            <div className="fv-help-block">
              <span role="alert">{formik.errors.email}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group */}
      <div className="fv-row mb-10">
        <div className="d-flex justify-content-between mt-n5">
          <div className="d-flex flex-stack mb-2">
            {/* begin::Label */}
            <label className="form-label fw-bolder text-dark fs-6 mb-0">Password</label>
            {/* end::Label */}
          </div>
        </div>
        <input type="password"
               autoComplete="off"
               placeholder="password"
               {...formik.getFieldProps('password')}
               className={clsx(
                 'form-control form-control-lg form-control-solid',
                 {
                   'is-invalid': formik.touched.password && formik.errors.password,
                 },
                 {
                   'is-valid': formik.touched.password && !formik.errors.password,
                 },
               )}
        />
        {formik.touched.password && formik.errors.password && (
          <div className="fv-plugins-message-container invalid-feedback">
            <div className="fv-help-block">
              <span role="alert">{formik.errors.password}</span>
            </div>
          </div>
        )}

        <div className="d-flex rs-flex-box-grid-end mt-2">
          <Link to={PATH_AUTH.forgotPassword} className="link-primary fs-6 fw-bolder">Mot de passe oublié
            ?</Link>
        </div>

      </div>
      {/* end::Form group */}


      {/* begin::Action */}
      <div className="text-center">
        <button
          type="submit"
          id="kt_sign_in_submit"
          className="btn btn-lg btn-primary w-100 mb-5"
          disabled={formik.isSubmitting || !formik.isValid}
        >
          {!loading && <span className="indicator-label">Continue</span>}
          {loading && (
            <span className="indicator-progress" style={{display: 'block'}}>
              Please wait...
              <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
            </span>
          )}
        </button>
      </div>
      {/* end::Action */}
    </form>
  )
}
